import React, { useEffect, useState } from "react";
import { getAllShipments } from "../api/shipmentService";
import {
    getParcels,
    createParcel,
    updateParcel,
    deleteParcel
} from "../api/parcelService";
const emptyForm = {
    id: null,
    trackingNumber: "",
    sender: "",
    receiver: "",
    status: "",
    cargoTypeId: "",
    shipmentId: "",
    updatedByUserId: ""
};

const ParcelPage = () => {
    const [parcels, setParcels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [formData, setFormData] = useState(emptyForm);
    const [isEditMode, setIsEditMode] = useState(false);
    const [shipments, setShipments] = useState([]);
    const fetchShipments = async () => {
        try {
            const data = await getAllShipments();
            setShipments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError("Error loading shipments.");
        }
    };
    useEffect(() => {
        fetchParcels();
        fetchShipments();
    }, [])

    const fetchParcels = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await getParcels();
            setParcels(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError("Error loading parcel list.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const resetForm = () => {
        setFormData(emptyForm);
        setIsEditMode(false);
    };

    const buildPayload = () => {
        const payload = {
            trackingNumber: formData.trackingNumber.trim(),
            sender: formData.sender.trim(),
            receiver: formData.receiver.trim(),
            status: formData.status.trim(),
            cargoTypeId: formData.cargoTypeId ? Number(formData.cargoTypeId) : null,
            shipmentId: formData.shipmentId ? Number(formData.shipmentId) : null
        };

        if (isEditMode) {
            payload.updatedByUserId = formData.updatedByUserId
                ? Number(formData.updatedByUserId)
                : null;
        }

        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        try {
            const payload = buildPayload();

            if (!payload.trackingNumber) {
                setError("Tracking number is required.");
                return;
            }

            if (isEditMode && formData.id) {
                await updateParcel(formData.id, payload);
                setSuccess("Parcel updated successfully.");
            } else {
                await createParcel(payload);
                setSuccess("Parcel created successfully.");
            }

            resetForm();
            await fetchParcels();
        } catch (err) {
            console.error(err);
            setError("Error saving parcel.");
        }
    };

    const handleEdit = (parcel) => {
        setFormData({
            id: parcel.id ?? null,
            trackingNumber: parcel.trackingNumber ?? "",
            sender: parcel.sender ?? "",
            receiver: parcel.receiver ?? "",
            status: parcel.status ?? "",
            cargoTypeId: parcel.cargoTypeId ?? "",
            shipmentId: parcel.shipmentId ?? "",
            updatedByUserId: parcel.updatedByUserId ?? ""
        });
        setIsEditMode(true);
        setError("");
        setSuccess("");
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this parcel?")) return;

        try {
            setError("");
            setSuccess("");
            await deleteParcel(id);
            setSuccess("Parcel deleted successfully.");
            await fetchParcels();
        } catch (err) {
            console.error(err);
            setError("Error deleting parcel.");
        }
    };

    if (loading) return <p>Loading...</p>;

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow">
            <h1 className="text-xl font-bold mb-4">Parcels</h1>

            {error && <p className="text-red-500 mb-3">{error}</p>}
            {success && <p className="text-green-600 mb-3">{success}</p>}

            <form onSubmit={handleSubmit} className="space-y-2 mb-6">
                <input
                    type="text"
                    name="trackingNumber"
                    placeholder="Tracking number"
                    value={formData.trackingNumber}
                    onChange={handleChange}
                    className="border px-3 py-2 rounded w-full"
                    required
                />

                <input
                    type="text"
                    name="sender"
                    placeholder="Sender"
                    value={formData.sender}
                    onChange={handleChange}
                    className="border px-3 py-2 rounded w-full"
                />

                <input
                    type="text"
                    name="receiver"
                    placeholder="Receiver"
                    value={formData.receiver}
                    onChange={handleChange}
                    className="border px-3 py-2 rounded w-full"
                />

                <input
                    type="text"
                    name="status"
                    placeholder="Status"
                    value={formData.status}
                    onChange={handleChange}
                    className="border px-3 py-2 rounded w-full"
                />

                <input
                    type="number"
                    name="cargoTypeId"
                    placeholder="CargoType ID"
                    value={formData.cargoTypeId}
                    onChange={handleChange}
                    className="border px-3 py-2 rounded w-full"
                />

                <select
                    name="shipmentId"
                    value={formData.shipmentId}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                >
                    <option value="">Select shipment</option>

                    {shipments.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.trackingNumber} (ID: {s.id})
                        </option>
                    ))}
                </select>

                <input
                    type="number"
                    name="updatedByUserId"
                    placeholder="UpdatedByUser ID"
                    value={formData.updatedByUserId}
                    onChange={handleChange}
                    className="border px-3 py-2 rounded w-full"
                />

                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                    type="submit"
                >
                    {isEditMode ? "Update" : "Create"}
                </button>

                {isEditMode && (
                    <button
                        type="button"
                        className="ml-2 bg-gray-300 px-4 py-2 rounded"
                        onClick={resetForm}
                    >
                        Cancel
                    </button>
                )}
            </form>

            <ul className="space-y-2">
                {parcels.map((parcel) => (
                    <li
                        key={parcel.id}
                        className="flex justify-between items-center border-b py-3"
                    >
                        <span>
                            <b>Tracking:</b> {parcel.trackingNumber} |{" "}
                            <b>Sender:</b> {parcel.sender || "�"} |{" "}
                            <b>Receiver:</b> {parcel.receiver || "�"} |{" "}
                            <b>Status:</b> {parcel.status || "�"}
                        </span>

                        <span>
                            <button
                                className="text-yellow-600 mr-2"
                                onClick={() => handleEdit(parcel)}
                            >
                                Edit
                            </button>
                            <button
                                className="text-red-600"
                                onClick={() => handleDelete(parcel.id)}
                            >
                                Delete
                            </button>
                        </span>
                    </li>
                ))}
            </ul>

            {!parcels.length && (
                <p className="text-gray-500 mt-4">No parcels found.</p>
            )}
        </div>
    );
};

export default ParcelPage;